import { Box, Typography } from "@mui/material";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { ReactNode } from "react";
import { fs } from "./tokens";
import { ScorerSummary, fmtBaht } from "./derive";

interface CardProps {
  icon: ReactNode;
  label: string;
  accent: string;
  accentBg: string;
  accentLine: string;
  value: ReactNode;
  foot: ReactNode;
}

function Card({ icon, label, accent, accentBg, accentLine, value, foot }: CardProps) {
  return (
    <Box
      sx={{
        flex: "1 1 200px",
        minWidth: 200,
        bgcolor: fs.surface,
        border: `1px solid ${accentLine}`,
        borderRadius: 3,
        p: 2,
        boxShadow: fs.shadow,
        display: "flex",
        flexDirection: "column",
        gap: 0.75,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Box
          sx={{
            width: 30,
            height: 30,
            borderRadius: 2,
            bgcolor: accentBg,
            color: accent,
            display: "grid",
            placeItems: "center",
          }}
        >
          {icon}
        </Box>
        <Typography sx={{ fontSize: 13, color: fs.dim, fontWeight: 600 }}>
          {label}
        </Typography>
      </Box>
      <Typography sx={{ fontSize: 30, fontWeight: 700, color: fs.text, lineHeight: 1.1 }}>
        {value}
      </Typography>
      <Typography sx={{ fontSize: 12, color: fs.faint }}>{foot}</Typography>
    </Box>
  );
}

export default function SummaryCards({ summary }: { summary: ScorerSummary }) {
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
      <Card
        icon={<SwapHorizIcon fontSize="small" />}
        label="ควรสลับสูตร"
        accent={fs.pos}
        accentBg={fs.posBg}
        accentLine={fs.posLine}
        value={
          <>
            {summary.switchCount}
            <Typography component="span" sx={{ fontSize: 14, color: fs.dim, ml: 0.5 }}>
              ราย
            </Typography>
          </>
        }
        foot={
          <>
            กำไรเพิ่มรวม{" "}
            <b style={{ color: fs.pos }}>+฿{fmtBaht(summary.switchBahtPerRound)}</b> / รอบ
          </>
        }
      />
      <Card
        icon={<HourglassEmptyIcon fontSize="small" />}
        label="รอการยืนยัน"
        accent={fs.amber}
        accentBg={fs.amberSoft}
        accentLine={fs.amberLine}
        value={
          <>
            {summary.pending}
            <Typography component="span" sx={{ fontSize: 14, color: fs.dim, ml: 0.5 }}>
              ราย
            </Typography>
          </>
        }
        foot={
          <>
            ยืนยันแล้ว {summary.confirmed} · ปฏิเสธ {summary.rejected}
          </>
        }
      />
      <Card
        icon={<CheckCircleOutlineIcon fontSize="small" />}
        label="แนะนำคงเดิม"
        accent={fs.keep}
        accentBg={fs.keepBg}
        accentLine={fs.keepLine}
        value={
          <>
            {summary.keep}
            <Typography component="span" sx={{ fontSize: 14, color: fs.dim, ml: 0.5 }}>
              ราย
            </Typography>
          </>
        }
        foot="สูตรเดิมยังดีที่สุด"
      />
      <Card
        icon={<HelpOutlineIcon fontSize="small" />}
        label="ข้อมูลไม่พอ"
        accent={fs.muted}
        accentBg={fs.mutedBg}
        accentLine={fs.mutedLine}
        value={
          <>
            {summary.insufficient}
            <Typography component="span" sx={{ fontSize: 14, color: fs.dim, ml: 0.5 }}>
              ราย
            </Typography>
          </>
        }
        foot="ยังไม่ประเมิน — รอรอบ/ช่วงวันเพิ่ม"
      />
    </Box>
  );
}
